import type { SystemRequirementsReport } from "./provider";

// Chrome clamps navigator.deviceMemory to [0.25, 8] per W3C spec; thresholds above 8 are unreachable.
export const MIN_DEVICE_MEMORY_GB = 4;
export const MIN_HARDWARE_CONCURRENCY = 4;

interface NavigatorWithDeviceMemory extends Navigator {
  deviceMemory?: number;
}

export async function checkSystemRequirements(): Promise<SystemRequirementsReport> {
  const nav = globalThis.navigator as NavigatorWithDeviceMemory | undefined;
  const warnings: string[] = [];

  const hardwareConcurrency = nav?.hardwareConcurrency;
  if (
    typeof hardwareConcurrency === "number" &&
    hardwareConcurrency > 0 &&
    hardwareConcurrency < MIN_HARDWARE_CONCURRENCY
  ) {
    warnings.push(
      `Needs at least ${MIN_HARDWARE_CONCURRENCY} CPU cores; found ${hardwareConcurrency}.`
    );
  }

  // `storage.estimate().quota` is the per-origin storage budget, not literal free
  // disk — a low quota on a high-free-disk machine would produce false-positive
  // blocks if used as a gate. Captured for diagnostics only; Chrome's own
  // `LanguageModel.create()` surfaces real disk-space failures.
  let storageQuotaBytes: number | undefined;
  if (nav?.storage && typeof nav.storage.estimate === "function") {
    try {
      const estimate = await nav.storage.estimate();
      storageQuotaBytes = estimate.quota;
    } catch {
      // best-effort; leave storageQuotaBytes undefined
    }
  }

  const deviceMemoryGb = nav?.deviceMemory;
  if (typeof deviceMemoryGb === "number" && deviceMemoryGb < MIN_DEVICE_MEMORY_GB) {
    warnings.push(
      `Needs at least ${MIN_DEVICE_MEMORY_GB} GB RAM (or ${MIN_DEVICE_MEMORY_GB} GB VRAM); device reports ${deviceMemoryGb} GB.`
    );
  }

  return {
    ok: warnings.length === 0,
    storageQuotaBytes,
    deviceMemoryGb,
    hardwareConcurrency,
    warnings,
  };
}
